import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import type { ProjectMemberItem, TaskStatusItem, SprintItem } from '@hygge/shared';
import { ChevronDown, Search, X } from 'lucide-react';
import {
  UNASSIGNED,
  emptyFilters,
  hasActiveClientFilters,
  toggleInArray,
  type FilterState,
} from '../lib/task-filter';

// Re-export the shared filter model so existing importers keep resolving through
// this module (the model + pure predicate live in ../lib/task-filter).
export {
  UNASSIGNED,
  emptyFilters,
  activeFilterCount,
  hasActiveClientFilters,
  taskMatchesFilters,
} from '../lib/task-filter';
export type { FilterState } from '../lib/task-filter';

/**
 * Server-side slice of the filter state. Dimension filters (status/priority/type/
 * assignee/label/overdue) are applied client-side over the loaded task set, so
 * only `search` and `sprintId` are forwarded to the tasks query.
 */
export function toQueryFilters(f: FilterState) {
  const q: Record<string, string | undefined> = {};
  if (f.search) q.search = f.search;
  if (f.sprintId !== 'all') q.sprintId = f.sprintId;
  return q;
}

const PRIORITY_OPTIONS: Option[] = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
  { value: 'none', label: 'None' },
];

const TYPE_OPTIONS: Option[] = [
  { value: 'task', label: 'Task' },
  { value: 'bug', label: 'Bug' },
  { value: 'story', label: 'Story' },
  { value: 'epic', label: 'Epic' },
];

interface Option {
  value: string;
  label: string;
  color?: string;
}

interface TaskFiltersBarProps {
  filters: FilterState;
  onChange: (filters: FilterState) => void;
  statuses: TaskStatusItem[];
  sprints?: SprintItem[];
  members: ProjectMemberItem[];
  /** Available label/tag names for the Label dimension. */
  labelOptions?: string[];
  /** Rows on screen after the client-side dimension filters. */
  shownCount: number;
  /** Size of the loaded set the filters ran over — not the board's total. */
  totalCount: number;
  /**
   * True when the loaded set is only the first page of a larger board. Without
   * this, "Showing 100 of 100" reads as "that is the whole board".
   */
  truncated?: boolean;
}

function MultiSelectPopover({
  label,
  options,
  selected,
  onToggle,
  emptyText = 'No options',
}: {
  label: string;
  options: Option[];
  selected: string[];
  onToggle: (value: string) => void;
  emptyText?: string;
}) {
  const count = selected.length;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'h-8 gap-1.5 text-xs font-normal cursor-pointer',
            count > 0 && 'border-hygge/40 text-hygge hover:text-hygge',
          )}
        >
          {label}
          {count > 0 && (
            <span className="rounded-full bg-hygge/15 px-1.5 text-[10px] font-medium text-hygge">
              {count}
            </span>
          )}
          <ChevronDown className="h-3 w-3 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-1.5">
        <div className="max-h-64 overflow-y-auto">
          {options.length === 0 ? (
            <p className="px-2 py-4 text-center text-xs text-muted-foreground">{emptyText}</p>
          ) : (
            options.map((opt) => {
              const checked = selected.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={checked}
                  onClick={() => onToggle(opt.value)}
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-xs hover:bg-accent cursor-pointer"
                >
                  <Checkbox checked={checked} className="pointer-events-none" tabIndex={-1} />
                  {opt.color && (
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ backgroundColor: opt.color }}
                    />
                  )}
                  <span className="truncate">{opt.label}</span>
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function TaskFiltersBar({
  filters,
  onChange,
  statuses,
  sprints,
  members,
  labelOptions = [],
  shownCount,
  totalCount,
  truncated = false,
}: TaskFiltersBarProps) {
  const formatName = useEmployeeNameFormatter();

  const update = (partial: Partial<FilterState>) => onChange({ ...filters, ...partial });
  const toggle = (key: 'statusIds' | 'priorities' | 'types' | 'assigneeIds' | 'labels', value: string) =>
    update({ [key]: toggleInArray(filters[key], value) } as Partial<FilterState>);

  const statusOptions: Option[] = statuses.map((s) => ({ value: s.id, label: s.name, color: s.color }));
  const assigneeOptions: Option[] = [
    { value: UNASSIGNED, label: 'Unassigned' },
    ...members.map((m) => ({
      value: m.employeeId,
      label: formatName({ firstName: m.firstName, lastName: m.lastName }),
    })),
  ];
  const labelSelectOptions: Option[] = labelOptions.map((l) => ({ value: l, label: l }));

  // Resolve selected values → removable chips.
  const statusName = (id: string) => statuses.find((s) => s.id === id)?.name ?? id;
  const priorityLabel = (v: string) => PRIORITY_OPTIONS.find((o) => o.value === v)?.label ?? v;
  const typeLabel = (v: string) => TYPE_OPTIONS.find((o) => o.value === v)?.label ?? v;
  const assigneeLabel = (id: string) => {
    if (id === UNASSIGNED) return 'Unassigned';
    const m = members.find((x) => x.employeeId === id);
    return m ? formatName({ firstName: m.firstName, lastName: m.lastName }) : id;
  };

  const chips: { key: string; label: string; onRemove: () => void }[] = [
    ...filters.statusIds.map((id) => ({
      key: `status-${id}`,
      label: statusName(id),
      onRemove: () => toggle('statusIds', id),
    })),
    ...filters.priorities.map((v) => ({
      key: `priority-${v}`,
      label: priorityLabel(v),
      onRemove: () => toggle('priorities', v),
    })),
    ...filters.types.map((v) => ({
      key: `type-${v}`,
      label: typeLabel(v),
      onRemove: () => toggle('types', v),
    })),
    ...filters.assigneeIds.map((id) => ({
      key: `assignee-${id}`,
      label: assigneeLabel(id),
      onRemove: () => toggle('assigneeIds', id),
    })),
    ...filters.labels.map((v) => ({
      key: `label-${v}`,
      label: v,
      onRemove: () => toggle('labels', v),
    })),
    ...(filters.overdue
      ? [{ key: 'overdue', label: 'Overdue', onRemove: () => update({ overdue: false }) }]
      : []),
  ];

  const clientActive = hasActiveClientFilters(filters);

  return (
    <div className="flex flex-col gap-2 border-b border-border bg-muted/10 px-6 py-2">
      <div className="flex flex-wrap items-center gap-2">
        {/* Search (server-side) */}
        <div className="relative min-w-[160px] max-w-xs flex-1">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={filters.search}
            onChange={(e) => update({ search: e.target.value })}
            placeholder="Search tasks..."
            className="h-8 pl-8 text-xs"
          />
        </div>

        <MultiSelectPopover
          label="Status"
          options={statusOptions}
          selected={filters.statusIds}
          onToggle={(v) => toggle('statusIds', v)}
          emptyText="No statuses"
        />
        <MultiSelectPopover
          label="Priority"
          options={PRIORITY_OPTIONS}
          selected={filters.priorities}
          onToggle={(v) => toggle('priorities', v)}
        />
        <MultiSelectPopover
          label="Type"
          options={TYPE_OPTIONS}
          selected={filters.types}
          onToggle={(v) => toggle('types', v)}
        />
        <MultiSelectPopover
          label="Assignee"
          options={assigneeOptions}
          selected={filters.assigneeIds}
          onToggle={(v) => toggle('assigneeIds', v)}
          emptyText="No members"
        />
        {labelSelectOptions.length > 0 && (
          <MultiSelectPopover
            label="Label"
            options={labelSelectOptions}
            selected={filters.labels}
            onToggle={(v) => toggle('labels', v)}
          />
        )}

        {/* Sprint (server-side, single select) */}
        {sprints && sprints.length > 0 && (
          <Select value={filters.sprintId} onValueChange={(v) => update({ sprintId: v })}>
            <SelectTrigger className="h-8 w-[120px] text-xs">
              <SelectValue placeholder="Sprint" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sprints</SelectItem>
              <SelectItem value="none">Backlog</SelectItem>
              {sprints.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Showing X of Y + Clear */}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            Showing {shownCount} of {totalCount}
            {truncated && ' loaded'}
          </span>
          {clientActive && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1 px-2 text-xs text-muted-foreground hover:text-foreground cursor-pointer"
              onClick={() => update({ ...emptyFilters(), search: filters.search, sprintId: filters.sprintId })}
            >
              <X className="h-3.5 w-3.5" />
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Selected value chips */}
      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {chips.map((chip) => (
            <Badge
              key={chip.key}
              variant="secondary"
              className="gap-1 py-0.5 pl-2 pr-1 text-[11px] font-normal"
            >
              <span className="truncate max-w-[140px]">{chip.label}</span>
              <button
                type="button"
                onClick={chip.onRemove}
                className="rounded-full p-0.5 hover:bg-foreground/10 cursor-pointer"
                aria-label={`Remove ${chip.label} filter`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
